import { useEffect, useState, useCallback, useMemo } from 'react';
import PageForm from 'components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import StyledInput from '../../components/styledInput/StyledInput';
import { SearchLoop } from '../../assets/icons';
import ApiDatabase from 'server';
import useContext from '../../hooks/useContext';
import EmptyState from '../../components/EmptyState/EmptyState';
import Pagination from '../../components/pagination/pagination';
import IdentityValidationMobileCard from '../../components/card/MobileListCard/IdentityValidationMobileCard';
import ValidatedDocumentCard from '../../components/card/MobileListCard/ValidatedDocumentCard';
import PillTabs from '../../components/tabs/PillTabs';
import useSlideOver from '../../components/SlideOver/useSlideOver';
import useModal from '../../components/modal/useModal';
import LeavingItem from '../../components/LeavingItem/LeavingItem';
import useLeavingItems from '../../hooks/useLeavingItems';
import useScrolledShadow from '../../hooks/useScrolledShadow';
import { capitalizeFirst } from '../../helper/helper';
import { DOC_TYPES, DOC_LABELS, MODAL_MAP, countIdentityDocumentProgress } from '../../helper/identityDocuments';
import {
  countComplementaryDocumentsWithStatus,
  getDisplayableComplementaryDocuments,
  hasPendingComplementaryDocument,
} from '../../helper/visaComplementaryDocuments';

const LIMIT = 20;

const getTempWorkerName = (tw) => {
  const user = tw?.user || tw;
  return `${capitalizeFirst(user?.firstname)} ${capitalizeFirst(user?.lastname)}`.trim() || 'N/A';
};

const isValidTempWorker = (tw) => {
  if (!tw) return false;
  const user = tw?.user || tw;
  return [user?.firstname, user?.lastname, user?.email].some(f => (f || '').toString().trim().length > 0);
};

const hasDocWithStatus = (tw, status) =>
  DOC_TYPES.some(t => Number(tw?.[t]?.validated) === status);

const countDocsWithStatus = (tw, status) =>
  DOC_TYPES.filter(t => Number(tw?.[t]?.validated) === status).length;

// A residence permit extension proof waiting for Jobbiz puts the temp worker in
// the pending list even when every identity file is already handled.
const isPendingForJobbiz = (tw) =>
  hasDocWithStatus(tw, 1) || hasPendingComplementaryDocument(tw);

const countPendingDocs = (tw) =>
  countDocsWithStatus(tw, 1) + countComplementaryDocumentsWithStatus(tw, '1');

const searchMatch = (tw, query) => {
  const user = tw?.user || tw;
  const f = [user?.firstname, user?.lastname, user?.email, user?.phone].map(v => (v || '').toLowerCase());
  return [...f, `${f[0]} ${f[1]}`].some(v => v.includes(query));
};

// ── Skeleton ──

const CardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 md:p-6 lg:p-8">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="h-5 skeleton-bar w-32" />
          <div className="h-6 skeleton-bar w-12" />
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="h-4 skeleton-bar w-40" />
          <div className="h-4 skeleton-bar w-28" />
        </div>
      </div>
    ))}
  </div>
);

// ── Component ──

const IdentityValidation = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingWorkers, setPendingWorkers] = useState([]);
  const [validatedWorkers, setValidatedWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isWaiting, setIsWaiting] = useState(true);
  const [isWaitingValidated, setIsWaitingValidated] = useState(true);

  const {
    updateTempWorker, setUpdateTempWorker, setObjID,
    setAdminTempWorkerId, setAdminUserId, setAdminType,
  } = useContext();
  const { toggleSlideOver } = useSlideOver();
  const { toggle } = useModal();
  const token = localStorage.getItem('xsrfToken');
  const { leave, isLeaving, onLeaveEnd } = useLeavingItems();
  const { ref: listScrollRef, isScrolled: isListScrolled } = useScrolledShadow();

  // ── Data fetching ──

  const getTempWorkers = useCallback(() => {
    setIsWaiting(true);
    ApiDatabase.getAllUsersWithIdentityFilesToComplete(
      { token },
      (data) => {
        setPendingWorkers((data || []).filter(tw => isValidTempWorker(tw) && isPendingForJobbiz(tw)));
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); setIsWaiting(false); }
    );
  }, [token]);

  const getValidatedWorkers = useCallback(() => {
    setIsWaitingValidated(true);
    ApiDatabase.getTempWorkersValidatedByAI(
      { token },
      (data) => {
        setValidatedWorkers((data || []).filter(tw => isValidTempWorker(tw) && DOC_TYPES.some(t => tw?.[t]?.validatedBy === 'ai')));
        setIsWaitingValidated(false);
      },
      (err) => { if (err.name !== 'CanceledError') console.error(err); setIsWaitingValidated(false); }
    );
  }, [token]);

  useEffect(() => { getTempWorkers(); getValidatedWorkers(); }, [getTempWorkers, getValidatedWorkers]);

  // ── Flatten validated workers → 1 row per AI-validated document ──

  const validatedDocRows = useMemo(() => {
    const rows = [];
    validatedWorkers.forEach(tw => {
      const name = getTempWorkerName(tw);
      DOC_TYPES.forEach(type => {
        const doc = tw?.[type];
        if (!doc || doc.validatedBy !== 'ai') return;
        let label = DOC_LABELS[type];
        if (type === 'identity' && tw.identity?.type) {
          label = tw.identity.type === 'CNI' ? 'Carte d\'identité' : 'Passeport';
        }
        rows.push({
          key: `${tw._id}-${type}`,
          name,
          documentType: type,
          docType: label,
          reason: doc.reason || '',
          date: doc.updated ? new Date(doc.updated) : null,
          tempWorker: tw,
        });
      });
    });
    rows.sort((a, b) => (b.date || 0) - (a.date || 0));
    return rows;
  }, [validatedWorkers]);

  // ── Filtered lists ──

  const filteredPending = useMemo(() => {
    if (!search.trim()) return pendingWorkers;
    const s = search.toLowerCase().trim();
    return pendingWorkers.filter(tw => searchMatch(tw, s));
  }, [search, pendingWorkers]);

  const filteredDocRows = useMemo(() => {
    if (!search.trim()) return validatedDocRows;
    const s = search.toLowerCase().trim();
    return validatedDocRows.filter(r => r.name.toLowerCase().includes(s) || r.docType.toLowerCase().includes(s));
  }, [validatedDocRows, search]);

  // ── Update handler ──

  useEffect(() => {
    if (!updateTempWorker) return;
    const { tempWorkerId, documentType, action, tempWorker: updatedTempWorker } = typeof updateTempWorker === 'object'
      ? updateTempWorker : { tempWorkerId: updateTempWorker, documentType: null, action: 'accept' };
    const newStatus = action === 'refuse' ? 3 : 2;
    const isComplementaryDocument = documentType === 'visa_complementary';
    const isTarget = (tw) => String(tw?._id) === String(tempWorkerId);

    const applyUpdate = (tw) => {
      const updated = { ...tw };
      if (isComplementaryDocument) {
        // The list item carries its own user fields: only the proofs are taken from the answer.
        updated.visa = {
          ...updated.visa,
          complementaryDocuments:
            updatedTempWorker?.visa?.complementaryDocuments ?? updated.visa?.complementaryDocuments ?? [],
        };
      } else if (documentType && updated[documentType]) {
        updated[documentType] = { ...updated[documentType], validated: newStatus, updated: Date.now() };
      }
      return updated;
    };

    // The card shows its new progress while it folds away, then leaves the list.
    const target = pendingWorkers.find(isTarget);
    setPendingWorkers(prev => prev.map(tw => (isTarget(tw) ? applyUpdate(tw) : tw)));
    if (target && !isPendingForJobbiz(applyUpdate(target))) {
      leave(String(tempWorkerId), () =>
        setPendingWorkers(prev => prev.filter(tw => !isTarget(tw) || isPendingForJobbiz(tw)))
      );
    }

    // The AI-validated tab never lists extension proofs.
    if (action !== 'refuse' && !isComplementaryDocument) getValidatedWorkers();
    setUpdateTempWorker(false);
  }, [updateTempWorker]);

  // ── Pagination ──

  const isPending = activeTab === 'pending';
  const currentList = isPending ? filteredPending : filteredDocRows;
  const currentIsWaiting = isPending ? isWaiting : isWaitingValidated;
  const totalPages = Math.ceil(currentList.length / LIMIT);
  const startIdx = (currentPage - 1) * LIMIT;
  const paginated = currentList.slice(startIdx, startIdx + LIMIT);

  const pendingDocCount = filteredPending.reduce((s, tw) => s + countPendingDocs(tw), 0);
  const paginationText = isPending
    ? `Affichage de ${paginated.length} intérimaire${paginated.length > 1 ? 's' : ''} sur ${currentList.length} (${pendingDocCount} document${pendingDocCount > 1 ? 's' : ''} à vérifier au total)`
    : `Affichage de ${paginated.length} document${paginated.length > 1 ? 's' : ''} sur ${currentList.length}`;

  // ── Tabs config ──

  const tabs = [
    { id: 'pending', label: 'En attente', count: pendingWorkers.length },
    { id: 'validated_ai', label: 'Validé par IA', count: validatedDocRows.length },
  ];

  // ── Actions ──

  const openTempWorkerInfo = (tw) => {
    const userId = tw?.id_user || tw?.user?._id || tw?._id;
    if (userId) { setObjID(userId); toggleSlideOver('get_temp_worker'); }
  };

  const openDocumentModal = (row) => {
    const tw = row.tempWorker;
    if (!tw?._id || !tw?.id_user) return;
    setAdminTempWorkerId(tw._id);
    setAdminUserId(tw.id_user);
    setAdminType(row.documentType);
    const modal = MODAL_MAP[row.documentType];
    if (modal) toggle(modal);
  };

  const countAcceptedFiles = (tw) => {
    const { accepted, total } = countIdentityDocumentProgress(tw);
    const complementaryDocuments = getDisplayableComplementaryDocuments(tw);
    const handledComplementary = complementaryDocuments.filter(
      (document) => String(document.validated) !== '1'
    ).length;
    return {
      accepted: accepted + handledComplementary,
      total: total + complementaryDocuments.length,
    };
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearch('');
    setCurrentPage(1);
  };

  // ── Render ──

  const renderContent = () => {
    if (currentIsWaiting) return <CardSkeleton />;
    if (paginated.length === 0) return <EmptyState type="identity_validation" />;

    // Keyed by tab so the cascade replays on each switch: the two tabs list
    // different cards, which remounted anyway.
    const cascade = (index) => ({
      className: 'motion-safe:animate-fadeUp',
      style: { animationDelay: `${Math.min(index, 9) * 30}ms` },
    });
    return (
      <div key={activeTab} className="flex flex-col gap-3 p-4 md:p-6 lg:p-6">
        {isPending
          ? paginated.map((tw, index) => {
              const { accepted, total } = countAcceptedFiles(tw);
              const leaving = isLeaving(String(tw._id));
              const { className: cascadeClassName, style: cascadeStyle } = cascade(index);
              return (
                <LeavingItem
                  key={tw._id}
                  leaving={leaving}
                  onAnimationEnd={onLeaveEnd(String(tw._id))}
                  className={`[--leave-gap:0.75rem] ${leaving ? '' : cascadeClassName}`}
                  style={cascadeStyle}
                >
                  <IdentityValidationMobileCard
                    tempWorker={tw}
                    nbFiles={accepted}
                    nbFilesTotal={total}
                    onOpenTempWorker={openTempWorkerInfo}
                  />
                </LeavingItem>
              );
            })
          : paginated.map((row, index) => (
              <div key={row.key} {...cascade(index)}>
                <ValidatedDocumentCard
                  name={row.name}
                  docType={row.docType}
                  reason={row.reason}
                  date={row.date}
                  onClick={() => openDocumentModal(row)}
                />
              </div>
            ))}
      </div>
    );
  };

  return (
    <PageForm>
      <SubNavbar elevated={isListScrolled}>
        <div className="flex justify-between items-center w-full gap-3">
          <div className="flex-1 max-w-[400px]">
            <StyledInput
              type="text"
              placeholder="Rechercher..."
              imgFront={<SearchLoop wh={16} color="#6B7280" />}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <PillTabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>
      </SubNavbar>

      <div className="flex flex-col w-full h-full overflow-hidden">
        <div ref={listScrollRef} className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </div>

      {!currentIsWaiting && totalPages >= 1 && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
          <Pagination
            currentPage={currentPage}
            countPerPage={paginated.length}
            countTotal={currentList.length}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            customText={paginationText}
          />
        </div>
      )}
    </PageForm>
  );
};

export default IdentityValidation;
